import { isArray } from '@ember/array';
import { type Registry as Services, service } from '@ember/service';
import type { Transition } from 'lumen/services/transition';
import type { FeatureToggle } from 'lumen/utils/constants/feature-toggle-constants';

type ProtectedRouteParams = {
  featureToggles?: FeatureToggle[] | FeatureToggle;
  features?: string[] | string;
  redirectTo?: string;
};

// TODO @vfuto: remove this when we have a better solution
interface ProtectedRoute {
  beforeModel: (transition: Transition) => Promise<unknown> | void;
}

export default function ({
  features,
  featureToggles,
  redirectTo,
}: ProtectedRouteParams) {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
  return function <T extends { new (...args: any[]): ProtectedRoute }>(
    Class: T,
  ) {
    class DecoratedClass extends Class {
      @service declare access: Services['access'];
      @service declare featureToggle: Services['feature-toggle'];
      @service declare router: Services['router'];
      @service declare transition: Services['transition'];

      // @ts-expect-error: Class 'ProtectedRoute' defines instance member property 'beforeModel', but extended class 'DecoratedClass' defines it as instance member function.
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      beforeModel(transition: Transition) {
        let hasAccess = true;

        if (features) {
          if (isArray(features)) {
            hasAccess = (features as string[]).some((feature) =>
              this.access.hasFeature(feature),
            );
          } else {
            hasAccess = this.access.hasFeature(features);
          }
        }

        if (hasAccess && featureToggles) {
          if (typeof featureToggles === 'string') {
            hasAccess = this.featureToggle.hasFeatureToggle(featureToggles);
          } else {
            hasAccess = featureToggles.every((featureToggle) =>
              this.featureToggle.hasFeatureToggle(featureToggle),
            );
          }
        }

        if (hasAccess) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          super.beforeModel(transition);
          return;
        }

        if (redirectTo) {
          this.router.transitionTo(redirectTo);
        } else {
          this.transition.redirectToEnabledTab(transition);
        }
      }
    }

    Object.defineProperty(DecoratedClass, 'name', { value: Class.name });

    return DecoratedClass;
  };
}
